import { DestroyRef, Injectable, OnDestroy, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Logger } from '@df/core/service/logger';
import { Search } from '@df/search/search.interfaces';
import { SearchService } from '@df/search/search.service';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AdvancedCommerce } from '../advanced-commerce.namespace';
import { AdvancedCommerceService } from '../advanced-commerce.service';
import { AdvancedCommerceSearchStore } from '../store/advanced-commerce-search.store';
import { AdvancedCommerceSearchResult } from './advanced-commerce-search-result';
import { AdvancedCommerceSearchResultFactoryService } from './advanced-commerce-search-result-factory.service';

@Injectable({
    providedIn: 'root'
})
export class AdvancedCommerceSearchService extends SearchService implements OnDestroy {
    protected searchStore = inject(AdvancedCommerceSearchStore);
    protected logger = inject(Logger);
    protected destroyRef = inject(DestroyRef);
    protected advancedCommerceService = inject(AdvancedCommerceService);

    protected response$ = toObservable(this.searchStore.response);
    protected responseSubscription?: Subscription;

    ngOnDestroy(): void {
        this.responseSubscription?.unsubscribe();
    }

    override search(request: Search.IRequest): Promise<AdvancedCommerceSearchResult> {
        return new Promise((resolve, reject) => {
            this.doSearch(request)
                // load models once found
                .then((response: AdvancedCommerce.Query.Result) => {
                    this.responseSubscription?.unsubscribe();

                    if (!request.autocomplete) {
                        this.advancedCommerceService.setPreviousSearches(request.q);
                    }

                    const searchResult = (this.searchResultFactoryService as AdvancedCommerceSearchResultFactoryService).create(
                        request.q,
                        response,
                        request
                    );

                    searchResult
                        .load()
                        .then(() => {
                            // resolve
                            resolve(searchResult);
                        })
                        .catch(reject);
                });
        });
    }

    /**
     * Sends search request
     */
    protected doSearch(request: Search.IRequest): Promise<Search.IResult> {
        return new Promise<Search.IResult>((resolve, reject) => {
            const responseId = this.searchStore.response()?.response_id;

            this.responseSubscription?.unsubscribe();

            this.responseSubscription = this.response$
                .pipe(filter(data => !!data?.elements?.[AdvancedCommerce.Query.Element.search] && responseId !== data.response_id))
                .subscribe(response => {
                    this.searchStore.searchRequestSet(undefined); // reset search request to prevent looping

                    resolve({
                        redirect: this.searchStore.redirect()?.url,
                        queryParams: this.searchStore.redirect()?.queryParams,
                        result: {
                            product: this.searchStore
                                .itemsEntities()
                                .filter(item => item.type === 'product')
                                .map(item => parseInt(item.id, 10)),
                            page: this.searchStore.pagesEntities().map(item => parseInt(item.id, 10)),
                            category: this.searchStore.categoriesEntities().map(item => parseInt(item.id, 10))
                        },
                        count: {
                            product: this.searchStore.totalProducts(),
                            page: this.searchStore.totalPages(),
                            category: this.searchStore.totalCategories()
                        }
                    });
                });
            // set `element` in store
            this.searchStore.elementSet(AdvancedCommerce.Query.Element.search);
            // set search request in store and trigger load
            this.searchStore.searchRequestSet(request);
        });
    }
}
